/** Display formatting helpers shared across public and admin pages. */
import type { Venue } from "./types";

/** Parse a date-only string ("2026-07-25") as UTC midnight to avoid TZ drift. */
function parseDay(iso: string): Date {
  return new Date(iso.includes("T") ? iso : `${iso}T00:00:00Z`);
}

function fmt(d: Date, opts: Intl.DateTimeFormatOptions): string {
  return d.toLocaleDateString(undefined, { timeZone: "UTC", ...opts });
}

/** "Jul 25–27, 2026", "Jul 30 – Aug 2, 2026", or full dates across years. */
export function formatDateRange(start: string, end: string): string {
  const s = parseDay(start);
  const e = parseDay(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start} – ${end}`;

  const full: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
  if (start === end) return fmt(s, full);
  if (s.getUTCFullYear() !== e.getUTCFullYear()) return `${fmt(s, full)} – ${fmt(e, full)}`;
  if (s.getUTCMonth() === e.getUTCMonth())
    return `${fmt(s, { month: "short", day: "numeric" })}–${e.getUTCDate()}, ${e.getUTCFullYear()}`;
  return `${fmt(s, { month: "short", day: "numeric" })} – ${fmt(e, { month: "short", day: "numeric" })}, ${e.getUTCFullYear()}`;
}

/** Baseball-style winning percentage: ".667", "1.000", or em dash when N/A. */
export function formatPct(v: number | null): string {
  if (v === null) return "—";
  return v.toFixed(3).replace(/^0/, "");
}

/** A Google Maps search for a plain address — the fallback when no link was given. */
export function mapsSearchHref(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export function venueMapsHref(v: Venue): string {
  return v.mapsUrl ?? mapsSearchHref(v.address);
}

/**
 * "Venue · Field" for a game, with a map link when the venue can produce one.
 *
 * Null when a game names neither, which is how a caller knows to render no
 * location line at all rather than an empty one. A game carrying only a field
 * — the Custom location an admin types for an ad-hoc site — still gets a line;
 * the field name IS the answer there, and there is nothing to map it to.
 *
 * Takes the venue list rather than a bundle so the admin console, the public
 * schedule, and the public bracket all read one implementation.
 */
export function gameLocation(
  venues: Venue[],
  game: { venueId?: string; field?: string },
): { text: string; href?: string } | null {
  if (!game.venueId && !game.field) return null;
  const venue = venues.find((v) => v.id === game.venueId);
  const parts = [venue?.name, game.field].filter(Boolean);
  const href = venue?.mapsUrl ?? (venue?.address ? mapsSearchHref(venue.address) : undefined);
  return { text: parts.join(" · ") || "Location TBD", href };
}

/**
 * A game's start time in the tournament's own timezone — "10:00 AM" — and the
 * empty string when it has none, so callers can fall back to "TBD".
 *
 * The timezone is the event's, never the browser's: a parent watching from
 * another state should read the time printed on the schedule at the park.
 */
export function gameTime(iso: string | undefined, timeZone: string): string {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return "";
  return new Date(ms).toLocaleTimeString(undefined, {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  });
}

/** The day a game falls on in the tournament's timezone — "Sat, May 2". */
export function gameDay(iso: string | undefined, timeZone: string): string {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return "";
  return new Date(ms).toLocaleDateString(undefined, {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Day and time together — "Sat, May 2 · 10:00 AM" — for the places that show
 * one game on its own, with no date heading above it to inherit. A bracket
 * card is the case: its games can be days apart, and "10:00 AM" alone does not
 * say which morning.
 */
export function gameDayTime(iso: string | undefined, timeZone: string): string {
  return [gameDay(iso, timeZone), gameTime(iso, timeZone)].filter(Boolean).join(" · ");
}

/**
 * A name reduced to at most two initials, for the avatar chips that stand in
 * for a photo. Shared by the roster cards and the profile card so one coach is
 * the same two letters wherever they appear.
 */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

/** Is the tournament's registration window open right now? */
export function registrationOpen(t: {
  registration: { opensAt: string; closesAt: string } | null;
}): boolean {
  if (!t.registration) return false;
  const now = Date.now();
  return Date.parse(t.registration.opensAt) <= now && now <= Date.parse(t.registration.closesAt);
}
